import { useState } from 'react';
import {
  FileText,
  Download,
  Printer,
  Calendar,
  Users,
  Trophy,
  DollarSign,
  MapPin,
  Clock,
  FileSpreadsheet,
  Mail,
  Search,
  Zap,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';

// Report categories with their reports
const reportCategories = [
  {
    id: 'events',
    name: 'Event Reports',
    icon: Calendar,
    color: 'text-blue-600 bg-blue-100',
    reports: [
      { id: 'event-summary', name: 'Event Summary', description: 'Complete event overview with entries, results, and payouts', formats: ['pdf', 'xlsx'] },
      { id: 'event-schedule', name: 'Season Schedule', description: 'Full season calendar with venues and dates', formats: ['pdf', 'xlsx', 'csv'] },
      { id: 'event-entries', name: 'Entry List', description: 'All entries for a specific event', formats: ['pdf', 'xlsx'] },
      { id: 'event-results', name: 'Official Results', description: 'Final race results with positions and times', formats: ['pdf'] },
      { id: 'event-attendance', name: 'Attendance Report', description: 'Entry counts and participation by event', formats: ['xlsx', 'csv'] },
    ]
  },
  {
    id: 'points',
    name: 'Points & Standings',
    icon: Trophy,
    color: 'text-amber-600 bg-amber-100',
    reports: [
      { id: 'points-standings', name: 'Championship Standings', description: 'Current points standings with breakdown', formats: ['pdf', 'xlsx'] },
      { id: 'points-history', name: 'Points History', description: 'Race-by-race points accumulation', formats: ['xlsx', 'csv'] },
      { id: 'points-projection', name: 'Points Projection', description: 'Projected final standings scenarios', formats: ['pdf'] },
      { id: 'rookie-standings', name: 'Rookie Standings', description: 'Rookie of the year standings', formats: ['pdf', 'xlsx'] },
    ]
  },
  {
    id: 'financial',
    name: 'Financial Reports',
    icon: DollarSign,
    color: 'text-emerald-600 bg-emerald-100',
    reports: [
      { id: 'purse-summary', name: 'Purse Summary', description: 'Total purse distribution by event', formats: ['pdf', 'xlsx'] },
      { id: 'driver-earnings', name: 'Driver Earnings', description: 'Year-to-date earnings by driver', formats: ['xlsx', 'csv'] },
      { id: 'entry-fees', name: 'Entry Fee Collection', description: 'Entry fees collected by event', formats: ['xlsx'] },
      { id: 'deductions', name: 'Deductions Report', description: 'Tire, fuel, and other deductions', formats: ['xlsx', 'csv'] },
      { id: 'outstanding-balances', name: 'Outstanding Balances', description: 'Unpaid fees and fines', formats: ['pdf', 'xlsx'] },
    ]
  },
  {
    id: 'competitors',
    name: 'Competitor Reports',
    icon: Users,
    color: 'text-purple-600 bg-purple-100',
    reports: [
      { id: 'driver-roster', name: 'Driver Roster', description: 'Complete list of registered drivers', formats: ['pdf', 'xlsx', 'csv'] },
      { id: 'driver-stats', name: 'Driver Statistics', description: 'Wins, top 5s, top 10s, average finish', formats: ['pdf', 'xlsx'] },
      { id: 'team-roster', name: 'Team Roster', description: 'Teams and their drivers', formats: ['pdf', 'xlsx'] },
      { id: 'membership-status', name: 'Membership Status', description: 'Active and expired memberships', formats: ['xlsx', 'csv'] },
      { id: 'contact-list', name: 'Contact Directory', description: 'Driver and team contact information', formats: ['xlsx', 'csv'] },
    ]
  },
  {
    id: 'tracks',
    name: 'Track Reports',
    icon: MapPin,
    color: 'text-red-600 bg-red-100',
    reports: [
      { id: 'track-schedule', name: 'Track Schedule', description: 'Events by track/venue', formats: ['pdf', 'xlsx'] },
      { id: 'track-records', name: 'Track Records', description: 'Lap records and track bests', formats: ['pdf'] },
      { id: 'track-results', name: 'Track Results History', description: 'Historical results at each venue', formats: ['xlsx'] },
    ]
  },
  {
    id: 'operations',
    name: 'Operations',
    icon: Zap,
    color: 'text-orange-600 bg-orange-100',
    reports: [
      { id: 'transponder-list', name: 'Transponder Assignment', description: 'Transponder numbers by driver', formats: ['xlsx', 'csv'] },
      { id: 'pit-assignments', name: 'Pit Assignments', description: 'Pit stall assignments for events', formats: ['pdf', 'xlsx'] },
      { id: 'credential-list', name: 'Credential List', description: 'Credentials issued by event', formats: ['xlsx'] },
      { id: 'safety-equipment', name: 'Safety Equipment Log', description: 'Equipment expiration tracking', formats: ['xlsx'] },
    ]
  },
];

// Quick reports that are frequently used
const quickReports = [
  { id: 'points-standings', name: 'Current Standings', icon: Trophy, category: 'points' },
  { id: 'driver-roster', name: 'Driver Roster', icon: Users, category: 'competitors' },
  { id: 'event-schedule', name: 'Season Schedule', icon: Calendar, category: 'events' },
  { id: 'driver-earnings', name: 'Driver Earnings', icon: DollarSign, category: 'financial' },
];

// Recent reports (mock data)
const recentReports = [
  { id: 1, name: 'Championship Standings', generatedAt: '2025-12-09T14:30:00', format: 'pdf', size: '245 KB' },
  { id: 2, name: 'Knoxville Nationals Entry List', generatedAt: '2025-12-08T09:15:00', format: 'xlsx', size: '128 KB' },
  { id: 3, name: 'Driver Earnings YTD', generatedAt: '2025-12-07T16:45:00', format: 'xlsx', size: '89 KB' },
  { id: 4, name: 'Season Schedule', generatedAt: '2025-12-05T11:00:00', format: 'pdf', size: '156 KB' },
];

// Format badge component
function FormatBadge({ format }) {
  const colors = {
    pdf: 'text-red-600 bg-red-50 border-red-200',
    xlsx: 'text-emerald-600 bg-emerald-50 border-emerald-200',
    csv: 'text-blue-600 bg-blue-50 border-blue-200',
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${colors[format] || 'text-slate-600 bg-slate-50 border-slate-200'}`}>
      {format.toUpperCase()}
    </span>
  );
}

// Report card component
function ReportCard({ report, onGenerate }) {
  return (
    <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors group">
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-slate-800 group-hover:text-rms-accent transition-colors">
          {report.name}
        </h4>
        <p className="text-sm text-slate-500 mt-0.5">{report.description}</p>
        <div className="flex items-center gap-2 mt-2">
          {report.formats.map(format => (
            <FormatBadge key={format} format={format} />
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2 ml-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onGenerate(report, 'preview')}
        >
          <FileText className="w-4 h-4" />
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onGenerate(report, 'download')}
        >
          <Download className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// Category section component
function CategorySection({ category, onGenerate, expanded, onToggle }) {
  const Icon = category.icon;

  return (
    <Card>
      <CardHeader
        className="cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${category.color}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <CardTitle>{category.name}</CardTitle>
              <CardDescription>{category.reports.length} reports available</CardDescription>
            </div>
          </div>
          <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="pt-0">
          <div className="space-y-3">
            {category.reports.map(report => (
              <ReportCard
                key={report.id}
                report={report}
                onGenerate={onGenerate}
              />
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export default function Reports() {

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategory, setExpandedCategory] = useState('events');
  const [selectedFormat, setSelectedFormat] = useState('all');

  // Filter reports based on search
  const filteredCategories = reportCategories.map(category => ({
    ...category,
    reports: category.reports.filter(report => {
      const matchesSearch = report.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        report.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFormat = selectedFormat === 'all' || report.formats.includes(selectedFormat);
      return matchesSearch && matchesFormat;
    })
  })).filter(category => category.reports.length > 0);

  const handleGenerateReport = (report, action) => {
    console.log(`${action} report:`, report);
    // In a real app, this would trigger report generation
    alert(`Generating ${report.name}...`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
            <FileText className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h1 className="page-title">Reports</h1>
            <p className="page-subtitle">Generate and download series reports</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Search and Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search reports..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="w-40">
                  <Select
                    value={selectedFormat}
                    onChange={(e) => setSelectedFormat(e.target.value)}
                  >
                    <option value="all">All Formats</option>
                    <option value="pdf">PDF</option>
                    <option value="xlsx">Excel</option>
                    <option value="csv">CSV</option>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Report Categories */}
          <div className="space-y-4">
            {filteredCategories.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-600 mb-2">No Reports Found</h3>
                  <p className="text-sm text-slate-500">
                    Try adjusting your search or filters
                  </p>
                </CardContent>
              </Card>
            ) : (
              filteredCategories.map(category => (
                <CategorySection
                  key={category.id}
                  category={category}
                  onGenerate={handleGenerateReport}
                  expanded={expandedCategory === category.id}
                  onToggle={() => setExpandedCategory(
                    expandedCategory === category.id ? null : category.id
                  )}
                />
              ))
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Reports */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500" />
                Quick Reports
              </CardTitle>
              <CardDescription>Frequently used reports</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {quickReports.map(report => {
                  const Icon = report.icon;
                  return (
                    <button
                      key={report.id}
                      onClick={() => {
                        const fullReport = reportCategories
                          .find(c => c.id === report.category)
                          ?.reports.find(r => r.id === report.id);
                        if (fullReport) handleGenerateReport(fullReport, 'download');
                      }}
                      className="w-full flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors text-left"
                    >
                      <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-slate-500" />
                      </div>
                      <span className="font-medium text-slate-700">{report.name}</span>
                      <Download className="w-4 h-4 text-slate-400 ml-auto" />
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Recent Reports */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-500" />
                Recent Reports
              </CardTitle>
              <CardDescription>Previously generated</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentReports.map(report => (
                  <div
                    key={report.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-700 text-sm truncate">
                          {report.name}
                        </span>
                        <FormatBadge format={report.format} />
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                        <span>{formatDate(report.generatedAt)}</span>
                        <span>•</span>
                        <span>{report.size}</span>
                      </div>
                    </div>
                    <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Export Options */}
          <Card>
            <CardHeader>
              <CardTitle>Export Options</CardTitle>
              <CardDescription>Bulk export settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button variant="secondary" className="w-full justify-start">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Export All Data (Excel)
              </Button>
              <Button variant="secondary" className="w-full justify-start">
                <Mail className="w-4 h-4 mr-2" />
                Schedule Email Reports
              </Button>
              <Button variant="secondary" className="w-full justify-start">
                <Printer className="w-4 h-4 mr-2" />
                Print Queue
              </Button>
            </CardContent>
          </Card>

          {/* Stats */}
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="text-2xl font-bold text-slate-800">
                    {reportCategories.reduce((sum, cat) => sum + cat.reports.length, 0)}
                  </div>
                  <div className="text-xs text-slate-500">Available Reports</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="text-2xl font-bold text-slate-800">
                    {recentReports.length}
                  </div>
                  <div className="text-xs text-slate-500">Generated This Week</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
